import React from 'react'
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Pagination,
  IconButton,
  Box,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'

const UserTable = ({
  users,
  loading,
  paginatedUsers,
  searchTerm,
  page,
  setPage,
  totalPages,
  onEditClick,
  getUserStatus,
}) => {
  return (
    <>
      {/* Table Section */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: (theme) =>
            `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
          borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
          overflowX: 'auto',
          backgroundColor: 'background.paper',
        }}>
        <Table sx={{ minWidth: 650 }} aria-label="users table">
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'rgba(0, 0, 0, 0.02)'
                    : 'rgba(255, 255, 255, 0.05)',
              }}>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  color: 'text.primary',
                  py: { xs: 1.25, sm: 1.5, md: 1.75 },
                }}>
                Name
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  color: 'text.primary',
                  py: { xs: 1.25, sm: 1.5, md: 1.75 },
                }}>
                Username
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  color: 'text.primary',
                  py: { xs: 1.25, sm: 1.5, md: 1.75 },
                }}>
                Role
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  color: 'text.primary',
                  py: { xs: 1.25, sm: 1.5, md: 1.75 },
                }}>
                Status
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  color: 'text.primary',
                  py: { xs: 1.25, sm: 1.5, md: 1.75 },
                }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : paginatedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">
                    {searchTerm ? 'No users found matching your search' : 'No users found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((row) => {
                const status = getUserStatus(row)
                return (
                  <TableRow
                    key={row._id}
                    sx={{
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.02)'
                            : 'rgba(255, 255, 255, 0.03)',
                      },
                    }}>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        fontSize: {
                          xs: '0.875rem',
                          sm: '0.9375rem',
                          md: '1rem',
                        },
                        color: 'text.primary',
                        py: { xs: 1.25, sm: 1.5, md: 1.75 },
                      }}>
                      {row.name || 'N/A'}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: {
                          xs: '0.875rem',
                          sm: '0.9375rem',
                          md: '1rem',
                        },
                        color: 'text.primary',
                        py: { xs: 1.25, sm: 1.5, md: 1.75 },
                      }}>
                      {row.username || 'N/A'}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: {
                          xs: '0.875rem',
                          sm: '0.9375rem',
                          md: '1rem',
                        },
                        color: 'text.primary',
                        py: { xs: 1.25, sm: 1.5, md: 1.75 },
                      }}>
                      {row.role || 'Not Found'}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: { xs: 1.25, sm: 1.5, md: 1.75 },
                      }}>
                      <Chip
                        label={status}
                        size="small"
                        sx={{
                          backgroundColor:
                            status === 'Active'
                              ? (theme) =>
                                  theme.palette.mode === 'light'
                                    ? 'rgba(76, 175, 80, 0.1)'
                                    : 'rgba(76, 175, 80, 0.2)'
                              : (theme) =>
                                  theme.palette.mode === 'light'
                                    ? 'rgba(255, 152, 0, 0.1)'
                                    : 'rgba(255, 152, 0, 0.2)',
                          color:
                            status === 'Active'
                              ? (theme) =>
                                  theme.palette.mode === 'light'
                                    ? 'rgba(76, 175, 80, 0.9)'
                                    : 'rgba(129, 199, 132, 0.9)'
                              : (theme) =>
                                  theme.palette.mode === 'light'
                                    ? 'rgba(255, 152, 0, 0.9)'
                                    : 'rgba(255, 183, 77, 0.9)',
                          fontWeight: 500,
                          fontSize: {
                            xs: '0.75rem',
                            sm: '0.8125rem',
                            md: '0.875rem',
                          },
                          height: { xs: 24, sm: 26, md: 28 },
                          borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
                        }}
                      />
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        py: { xs: 1.25, sm: 1.5, md: 1.75 },
                      }}>
                      <Stack
                        direction="row"
                        spacing={{ xs: 0.5, sm: 1, md: 1.5 }}
                        justifyContent="flex-end">
                        <IconButton
                          size="small"
                          onClick={() => onEditClick(row._id)}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': {
                              color: 'primary.main',
                              backgroundColor: (theme) =>
                                theme.palette.mode === 'light'
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(144, 202, 249, 0.16)',
                            },
                            width: { xs: 32, sm: 36, md: 40 },
                            height: { xs: 32, sm: 36, md: 40 },
                          }}>
                          <EditIcon
                            sx={{
                              fontSize: {
                                xs: '1rem',
                                sm: '1.125rem',
                                md: '1.25rem',
                              },
                            }}
                          />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{
                            color: 'text.secondary',
                            '&:hover': {
                              color: 'error.main',
                              backgroundColor: (theme) =>
                                theme.palette.mode === 'light'
                                  ? 'rgba(244, 67, 54, 0.08)'
                                  : 'rgba(244, 67, 54, 0.16)',
                            },
                            width: { xs: 32, sm: 36, md: 40 },
                            height: { xs: 32, sm: 36, md: 40 },
                          }}>
                          <DeleteIcon
                            sx={{
                              fontSize: {
                                xs: '1rem',
                                sm: '1.125rem',
                                md: '1.25rem',
                              },
                            }}
                          />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: { xs: 2, sm: 2.5, md: 3 },
          }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
            size="small"
            sx={{
              '& .MuiPaginationItem-root': {
                fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                minWidth: { xs: 32, sm: 36, md: 40 },
                height: { xs: 32, sm: 36, md: 40 },
                '&.Mui-selected': {
                  fontWeight: 600,
                },
              },
            }}
          />
        </Box>
      )}
    </>
  )
}

export default UserTable
